import { HTMLAttributes, useEffect, useState } from 'react';
import { useLandingHomePage } from '@/website/hooks/landingPage/useLandingHomePage';
import { useGlobalStore } from '@/website/store/GlobalStore';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { InsuranceLandingPageEnum } from '@/website/types/insurerType';
import { getCountryNormalLogoUrl, getSupportTranslationCountryConvertMap, useJoveBreakpoint, useLocalTranslation } from '@jove-frontend/uikit';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';

type CommonSupportCountriesAcrossEuropeViewProps = HTMLAttributes<HTMLDivElement> & {
  insuranceType: InsuranceLandingPageEnum;
  info: {
    supportCountriesAcrossEuropeTitle: string;
    supportCountriesAcrossEuropeSubTitle: string;
  };
};
const CommonSupportCountriesAcrossEuropeView = (props: CommonSupportCountriesAcrossEuropeViewProps) => {
  const { className, info } = props;
  const { $t } = useLocalTranslation();
  const { isMobileLayout } = useJoveBreakpoint();
  const [countryList, setCountryList] = useState([]);
  const {
    state: { promotionInfo },
  } = promotionInfoStore();
  const { containerCustomStyles, contentContainerStyles } = useLandingHomePage();
  const {
    state: { canSwitchCountryList },
  } = useGlobalStore();

  /**
   * effects
   */
  useEffect(() => {
    const allList = Object.values(getSupportTranslationCountryConvertMap());
    if (promotionInfo) {
      setCountryList(
        allList
          .filter((item) => !['GB', 'US', 'CN'].includes(item.countryCode))
          .filter((item) => (promotionInfo?.countries || [])?.find((aItem) => aItem.code === item.countryCode))
      );
    } else {
      setCountryList(allList.filter((item) => !['GB', 'US', 'CN'].includes(item.countryCode)));
    }
  }, [canSwitchCountryList, promotionInfo, $t]);

  return (
    <Box className={clsx('flex flex-col items-center', className)}>
      <Box
        className={clsx('flex flex-col items-center', isMobileLayout ? 'space-y-6' : 'space-y-12')}
        sx={{ ...containerCustomStyles, ...contentContainerStyles }}
      >
        <Box className="flex flex-col items-center">
          <Typography
            component="h4"
            variant="h4"
            className="text-center text-4xl font-black"
            style={{ color: promotionInfo?.brandingData?.secondary_colour || '#202020' }}
          >
            {info?.supportCountriesAcrossEuropeTitle || $t('page:supportCountriesSection.supporting_countries_across_europe')}
          </Typography>
          <Typography
            component="h6"
            variant="subtitle1"
            className="mt-2 text-center text-lg font-medium"
            style={{ color: promotionInfo?.brandingData?.secondary_colour || '#202020' }}
          >
            {info?.supportCountriesAcrossEuropeSubTitle || $t('page:supportCountriesSection.provide_insurance_cover_for_companies_in')}
          </Typography>
        </Box>
        <Box className="flex flex-row flex-wrap gap-4">
          {countryList.map((item, index) => {
            return (
              <Box
                key={index}
                className={clsx('flex flex-row items-center space-x-2 rounded-full bg-white', isMobileLayout ? 'px-3 py-2' : 'px-6 py-2')}
                style={{ boxShadow: '0px 0px 10px 0px #00000014' }}
              >
                <img className="w-8" src={getCountryNormalLogoUrl(item.countryCode)} alt={item.countryCode} />
                <Typography component="h6" variant="subtitle1" className={clsx('text-primary-black font-bold', isMobileLayout ? '' : 'text-lg')}>
                  {item.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CommonSupportCountriesAcrossEuropeView;
